import type { ReactNode } from "react";
import type { Words } from "./copy";
import { LANGUAGES, type Language as Lang } from "../types";

/* The frame every customer page shares: the mark at the top, the language
   picker beside it, and the footer with who we are and where to write. Moved
   out of pages/Preview.tsx so the arrival page (pages/Arrived.tsx) is the same
   page to the person reading it. */

/** The address every mail comes from, and the one a customer can write to. */
export const CONTACT_EMAIL = "contact@minimewonder.com";

/** The site, in the reader's language: the same folders the site keeps them in,
 *  with English at the root. */
export function siteUrl(
  language: Lang,
  page: string,
  params?: Record<string, string>,
  hash?: string
): string {
  const folder = language === "en" ? "" : `${language}/`;
  const query = params ? `?${new URLSearchParams(params)}` : "";
  return `https://www.minimewonder.com/${folder}${page}${query}${hash ? `#${hash}` : ""}`;
}

/** A way to reach a person, with the subject already in their language and the
 *  link they are on in the body, which is how the mail finds its lead. */
export function Contact({ words: t, ask, style }: { words: Words; ask: string; style?: React.CSSProperties }) {
  const mail =
    `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t.t("contactSubject"))}` +
    `&body=${encodeURIComponent(`\n\n${window.location.href}`)}`;
  return (
    <p className="small muted" style={{ margin: "12px 0 0", ...style }}>
      <Spliced text={t.t(ask)} slot="{email}">
        <a href={mail}>{CONTACT_EMAIL}</a>
      </Spliced>
    </p>
  );
}

/** Who this is, somebody to write to, and what happens to the photo they sent:
 *  the privacy page the form linked to, in the language they are reading. */
export function Footer({
  words: t,
  language,
  contact,
}: {
  words: Words;
  language: Lang;
  /** Only where nothing above already says who to write to. */
  contact?: boolean;
}) {
  const quiet = { color: "inherit" };
  return (
    <div className="footer">
      {contact && <Contact words={t} ask="contactAsk" style={{ margin: 0 }} />}
      <p style={{ margin: contact ? "8px 0 0" : 0 }}>
        MiniME ·{" "}
        <a href={siteUrl(language, "")} target="_blank" rel="noopener" style={quiet}>
          minimewonder.com
        </a>{" "}
        ·{" "}
        <a href={siteUrl(language, "privacy.html")} target="_blank" rel="noopener" style={quiet}>
          {t.t("privacy")}
        </a>
      </p>
    </div>
  );
}

/** A sentence with something other than text in the middle of it, wherever
 *  its language puts it. */
export function Spliced({ text, slot, children }: { text: string; slot: string; children: ReactNode }) {
  const at = text.indexOf(slot);
  if (at < 0) {
    return (
      <>
        {text} {children}
      </>
    );
  }
  return (
    <>
      {text.slice(0, at)}
      {children}
      {text.slice(at + slot.length)}
    </>
  );
}

/** Which Chinese a browser tag reads: Traditional for the Hant script and
 *  for Taiwan, Hong Kong and Macau, Simplified for the Hans script, the
 *  mainland, Singapore, Malaysia and a bare "zh", as the site decides it. */
function chineseOf(tag: string): Lang {
  const t = tag.toLowerCase().replace(/_/g, "-");
  if (/-hant\b/.test(t)) return "zh";
  if (/-hans\b/.test(t)) return "zh-hans";
  return /-(tw|hk|mo)\b/.test(t) ? "zh" : "zh-hans";
}

/** What the browser says it reads, if it is one of the nine. */
export function guessLanguage(): Lang {
  const known: readonly string[] = LANGUAGES;
  for (const tag of navigator.languages ?? [navigator.language]) {
    const lower = (tag ?? "").toLowerCase();
    if (lower === "zh" || lower.startsWith("zh-") || lower.startsWith("zh_")) return chineseOf(lower);
    const short = lower.slice(0, 2);
    if (known.includes(short)) return short as Lang;
  }
  return "en";
}

export function Shell({ children, picker }: { children: ReactNode; picker?: ReactNode }) {
  return (
    <div className="wrap wrap-top page selectable">
      {/* The mark stays optically centred and the picker sits out at the edge:
          it is there for the minority who need it and should not read as the
          first decision on the page. */}
      <div className="preview-top">
        <img src="/logo.svg" alt="MiniME" height={30} />
        {picker && <div className="preview-top-right">{picker}</div>}
      </div>
      {children}
    </div>
  );
}
